import { create } from 'zustand';
import { ChatState, Chat, Message } from '../types';
import { apiService } from '../services/api';
import { useAuthStore } from './authStore';
import { audioNotificationManager } from '../utils/audio';
import toast from 'react-hot-toast';

// Local storage utility functions
const STORAGE_KEYS = {
  CHATS: 'boz_chats',
  MESSAGES: 'boz_messages',
  CHAT_METADATA: 'boz_chat_metadata',
  LAST_SYNC: 'boz_last_sync'
};

const saveToLocalStorage = (key: string, data: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Failed to save ${key} to localStorage:`, error);
  }
};

const loadFromLocalStorage = (key: string) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Failed to load ${key} from localStorage:`, error);
    return null;
  }
};

const clearLocalStorage = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to clear ${key} from localStorage:`, error);
  }
};

interface ChatStore extends ChatState {
  // Actions
  loadChats: (searchQuery?: string, type?: string, tripIds?: string[], forceRefresh?: boolean) => Promise<void>;
  loadAllChatsWithMessages: () => Promise<void>;
  loadMessages: (userId: number) => Promise<void>;
  loadUnreadChatMessages: () => Promise<void>;
  sendMessage: (userId: number, messageData: {
    message: string;
    type?: string;
    action?: string;
    inventoryId?: string;
  }) => Promise<void>;
  setActiveChat: (chat: Chat | null) => void;
  addMessage: (message: Message) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  updateChatStatus: (userId: number, status: 'pending' | 'read') => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  // Search state
  setSearchQuery: (query: string) => void;
  setFilterType: (type: string) => void;
  currentSearchQuery: string;
  currentFilterType: string;
  // Trip filter state
  selectedTripIds: string[];
  setSelectedTripIds: (tripIds: string[]) => void;
  loadSelectedTripIds: () => void;
  saveSelectedTripIds: (tripIds: string[]) => void;
  // Loading state for individual chats
  loadingChatId: number | null;
  setLoadingChatId: (chatId: number | null) => void;
  // Bulk loading progress state
  isBulkLoading: boolean;
  bulkLoadingProgress: number;
  bulkLoadingTotal: number;
  bulkLoadingCurrent: number;
  bulkLoadingMessage: string;
  setBulkLoadingProgress: (progress: number, total: number, current: number, message: string) => void;
  // Track chats that have been loaded before
  // Polling
  startPolling: () => void;
  stopPolling: () => void;
  startMessagePolling: (userId: number) => void;
  stopMessagePolling: () => void;
  isPolling: boolean;
  isMessagePolling: boolean;
  pollInterval: ReturnType<typeof setInterval> | null;
  messagePollInterval: ReturnType<typeof setInterval> | null;
  lastUpdatedOn: number | null;
  lastMessageUpdatedOn: { [userId: number]: number };
  // Audio notifications
  checkForUnreadChats: (newChats: Chat[]) => void;
  notifiedUnreadChats: Set<number>; // Track which chats have been notified
  // Local storage management
  clearLocalStorage: () => void;
  getCacheStats: () => {
    hasChats: boolean;
    chatCount: number;
    hasMessages: boolean;
    messageUserCount: number;
    lastSync: string;
  };
}

// Initialize selectedTripIds from localStorage
const initializeSelectedTripIds = (): string[] => {
  try {
    const savedTripIds = localStorage.getItem('selected_trip_ids');
    if (savedTripIds) {
      return savedTripIds.split(',').map(id => id.trim()).filter(id => id);
    }
  } catch (error) {
    console.error('Failed to load selected trip IDs from localStorage:', error);
  }
  return [];
};

let loadChatsVersion = 0;

export const useChatStore = create<ChatStore>((set, get) => ({
  // Initial state
  chats: [],
  activeChat: null,
  messages: {},
  isLoading: false,
  isSending: false,
  error: null,
  // Search state
  currentSearchQuery: '',
  currentFilterType: 'ALL',
  // Trip filter state
  selectedTripIds: initializeSelectedTripIds(),
  // Loading state for individual chats
  loadingChatId: null,
  // Bulk loading progress state
  isBulkLoading: false,
  bulkLoadingProgress: 0,
  bulkLoadingTotal: 0,
  bulkLoadingCurrent: 0,
  bulkLoadingMessage: '',
  // Polling state
  isPolling: false,
  isMessagePolling: false,
  pollInterval: null,
  messagePollInterval: null,
  lastUpdatedOn: null,
  lastMessageUpdatedOn: {},
  // Track notified unread chats
  notifiedUnreadChats: new Set<number>(),

  // Actions
  loadChats: async (searchQuery?: string, type?: string, tripIds?: string[], forceRefresh: boolean = false) => {
    const thisVersion = ++loadChatsVersion;
    set({ isLoading: true, error: null });

    try {
      // Normalize search parameters
      const normalizedSearchQuery = searchQuery?.trim() || undefined;
      const normalizedType = type === 'ALL' ? undefined : type;
      const normalizedTripIds = tripIds && tripIds.length > 0 ? tripIds : undefined;
      
      // Check if this is a default load (no search/filter parameters)
      const isDefaultLoad = !normalizedSearchQuery && !normalizedType && !normalizedTripIds;
      let chats: Chat[] = [];
      
      // Only use cache if not forcing refresh and it's a default load
      if (!forceRefresh && isDefaultLoad) {
        const cachedChats = loadFromLocalStorage(STORAGE_KEYS.CHATS);
        const lastSync = loadFromLocalStorage(STORAGE_KEYS.LAST_SYNC);
        
        // Use cache only if it's less than 30 seconds old
        if (cachedChats && Array.isArray(cachedChats) && lastSync && (Date.now() - lastSync) < 30000) {
          chats = cachedChats;
          console.log('Loaded chats from cache:', chats.length);
          
          // Still fetch fresh data in background for updates
          setTimeout(async () => {
            try {
              const freshChats = await apiService.getChats(normalizedSearchQuery, normalizedType, undefined, normalizedTripIds);
              if (thisVersion !== loadChatsVersion) return;
              if (freshChats && freshChats.length > 0) {
                set({ chats: freshChats });
                saveToLocalStorage(STORAGE_KEYS.CHATS, freshChats);
                saveToLocalStorage(STORAGE_KEYS.LAST_SYNC, Date.now());
                console.log('Background refresh completed:', freshChats.length);
              }
            } catch (error) {
              console.error('Background refresh failed:', error);
            }
          }, 100);
        }
      }
      
      // If no cached data, search/filter parameters, or force refresh, fetch from API
      if (chats.length === 0 || forceRefresh) {
        chats = await apiService.getChats(normalizedSearchQuery, normalizedType, undefined, normalizedTripIds);
        console.log('Fetched chats from API:', chats.length);
        
        // Save to cache if it's a default load
        if (isDefaultLoad) {
          saveToLocalStorage(STORAGE_KEYS.CHATS, chats);
          saveToLocalStorage(STORAGE_KEYS.LAST_SYNC, Date.now());
        }
      }
      
      // Set lastUpdatedOn to the most recent chat's lastMessageTime
      const mostRecentTime = chats.length > 0 
        ? Math.max(...chats.map(chat => chat.lastMessageTime))
        : null;
      
      // Sort chats by unread status first, then by lastMessageTime (newest first)
      const sortedChats = chats.sort((a, b) => {
        // First priority: unread chats (unreadCount > 0)
        const aHasUnread = a.unreadCount > 0;
        const bHasUnread = b.unreadCount > 0;
        
        if (aHasUnread && !bHasUnread) return -1;
        if (!aHasUnread && bHasUnread) return 1;
        
        // Second priority: lastMessageTime (newest first)
        return b.lastMessageTime - a.lastMessageTime;
      });

      set({
        chats: sortedChats,
        isLoading: false,
        error: null,
        lastUpdatedOn: mostRecentTime
      });

      // Check for unread chats and play notification if needed
      get().checkForUnreadChats(sortedChats);

      // Auto-load messages for unread chats if not in local storage
      get().loadUnreadChatMessages();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load chats';
      
      // Try to load from cache as fallback
      const cachedChats = loadFromLocalStorage(STORAGE_KEYS.CHATS);
      if (cachedChats && Array.isArray(cachedChats)) {
        console.log('Using cached chats as fallback');
        set({
          chats: cachedChats,
          isLoading: false,
          error: null
        });
      } else {
        set({
          isLoading: false,
          error: errorMessage
        });
        toast.error(errorMessage);
      }
    }
  },

  loadMessages: async (userId: number) => {
    const { messages } = get();
    const existingMessages = messages[userId] || [];
    const isFirstLoad = existingMessages.length === 0;
    
    console.log('loadMessages called for userId:', userId, 'existing messages:', existingMessages.length, 'isFirstLoad:', isFirstLoad);
    
    // Don't load if already loading
    if (get().isLoading) {
      console.log('Skipping load - already loading');
      return;
    }
    
    // Check local storage first and display immediately if available
    const cachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES);
    if (cachedMessages && cachedMessages[userId] && Array.isArray(cachedMessages[userId]) && cachedMessages[userId].length > 0) {
      // Display cached messages immediately
      console.log('Displaying cached messages immediately for user', userId, ':', cachedMessages[userId].length);
      set((state) => ({
        messages: {
          ...state.messages,
          [userId]: cachedMessages[userId]
        },
        isLoading: false,
        error: null,
        loadingChatId: null
      }));
    } else {
      // Only show loading state if no cached messages
      set({ 
        isLoading: true, 
        error: null, 
        loadingChatId: isFirstLoad ? userId : null 
      });
    }
    
    // Always fetch fresh messages from API immediately
    try {
      console.log('Fetching fresh messages from API for user', userId);
      const freshMessages = await apiService.getMessages(userId);
      console.log('Received fresh messages from API:', freshMessages.length);
      
      // Save to cache
      const allCachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES) || {};
      allCachedMessages[userId] = freshMessages;
      saveToLocalStorage(STORAGE_KEYS.MESSAGES, allCachedMessages);
      
      // Track the most recent message timestamp for this user
      const mostRecentTime = freshMessages.length > 0 
        ? Math.max(...freshMessages.map(msg => msg.createdOn))
        : Date.now();
      
      // Update store with fresh messages
      set((state) => ({
        messages: {
          ...state.messages,
          [userId]: freshMessages
        },
        isLoading: false,
        error: null,
        loadingChatId: null,
        lastMessageUpdatedOn: {
          ...state.lastMessageUpdatedOn,
          [userId]: mostRecentTime
        }
      }));
    } catch (error: any) {
      console.error('Error loading messages from API:', error);
      
      // If we had cached messages, keep them displayed
      // If we don't have cached messages, show error
      const cachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES);
      if (cachedMessages && cachedMessages[userId]) {
        console.log('API failed, keeping cached messages for user', userId);
        set((state) => ({
          messages: {
            ...state.messages,
            [userId]: cachedMessages[userId]
          },
          isLoading: false,
          error: null,
          loadingChatId: null
        }));
        // Show a subtle warning that we're showing cached data
        toast.error('Failed to refresh messages. Showing cached data.');
      } else {
        const errorMessage = error.message || 'Failed to load messages';
        set({ 
          isLoading: false, 
          error: errorMessage,
          loadingChatId: null
        });
        toast.error(errorMessage);
      }
    }
  },

  loadUnreadChatMessages: async () => {
    const { chats } = get();
    
    // Find all chats with unread messages
    const unreadChats = chats.filter(chat => chat.unreadCount > 0);
    
    if (unreadChats.length === 0) {
      return;
    }

    // Check local storage for cached messages
    const cachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES) || {};
    
    // Filter out chats that already have messages in cache
    const chatsToLoad = unreadChats.filter(chat => {
      const hasCachedMessages = cachedMessages[chat.userId] && Array.isArray(cachedMessages[chat.userId]) && cachedMessages[chat.userId].length > 0;
      return !hasCachedMessages;
    });

    if (chatsToLoad.length === 0) {
      console.log('All unread chats already have messages in cache');
      return;
    }

    console.log(`Auto-loading messages for ${chatsToLoad.length} unread chats without cached messages`);

    // Load messages for each unread chat asynchronously (don't block UI)
    await Promise.allSettled(chatsToLoad.map(async (chat) => {
      try {
        // Use a small delay to prevent overwhelming the API
        await new Promise(resolve => setTimeout(resolve, Math.random() * 500));

        console.log(`Loading messages for unread chat: ${chat.name} (userId: ${chat.userId})`);

        // Fetch messages from API
        const messages = await apiService.getMessages(chat.userId);

        // Update store with messages
        set((state) => ({
          messages: {
            ...state.messages,
            [chat.userId]: messages
          }
        }));

        // Save to cache
        const allCachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES) || {};
        allCachedMessages[chat.userId] = messages;
        saveToLocalStorage(STORAGE_KEYS.MESSAGES, allCachedMessages);

        console.log(`Loaded ${messages.length} messages for ${chat.name}`);
      } catch (error) {
        console.error(`Failed to load messages for unread chat ${chat.userId}:`, error);
        // Don't show error toast for background loading
      }
    }));
  },

  sendMessage: async (userId: number, messageData: {
    message: string;
    type?: string;
    action?: string;
    inventoryId?: string;
  }) => {
    const { user } = useAuthStore.getState();
    if (!user) {
      toast.error('User not authenticated');
      return;
    }

    // Create optimistic message
    const optimisticMessage: Message = {
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdOn: Date.now(),
      createdBy: user.id,
      createdByName: user.name,
      createdByNumber: user.phoneNumber,
      belongsToId: userId,
      byBoz: true,
      type: messageData.type || 'Text',
      version: '1.0',
      timestamp: new Date().toISOString(),
      status: 'sending',
      isOptimistic: true,
      sendMessage: {
        data: {
          message: messageData.message
        },
        userId: userId.toString(),
        fullPhoneNumber: user.phoneNumber,
        type: messageData.type || 'Text'
      }
    };

    // Add optimistic message to UI
    set((state) => {
      const newMessages = {
        ...state.messages,
        [userId]: [
          ...(state.messages[userId] || []),
          optimisticMessage
        ]
      };
      
      // Save to local storage
      const allCachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES) || {};
      allCachedMessages[userId] = newMessages[userId];
      saveToLocalStorage(STORAGE_KEYS.MESSAGES, allCachedMessages);
      
      return { messages: newMessages };
    });

    try {
      // Send message to API
      const response = await apiService.sendMessage(userId, messageData);
      
      // Update message status to sent
      set((state) => {
        const updatedMessages = {
          ...state.messages,
          [userId]: state.messages[userId]?.map(msg =>
            msg.id === optimisticMessage.id
              ? { ...msg, status: 'sent', isOptimistic: false }
              : msg
          ) || []
        };
        
        // Save to local storage
        const allCachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES) || {};
        allCachedMessages[userId] = updatedMessages[userId];
        saveToLocalStorage(STORAGE_KEYS.MESSAGES, allCachedMessages);
        
        return { messages: updatedMessages };
      });

      toast.success('Message sent');
    } catch (error: any) {
      console.error('Failed to send message:', error);
      
      // Update message status to failed
      set((state) => {
        const updatedMessages = {
          ...state.messages,
          [userId]: state.messages[userId]?.map(msg =>
            msg.id === optimisticMessage.id
              ? { ...msg, status: 'failed' }
              : msg
          ) || []
        };
        
        // Save to local storage
        const allCachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES) || {};
        allCachedMessages[userId] = updatedMessages[userId];
        saveToLocalStorage(STORAGE_KEYS.MESSAGES, allCachedMessages);
        
        return { messages: updatedMessages };
      });

      toast.error(error.message || 'Failed to send message');
    }
  },

  setActiveChat: (chat: Chat | null) => {
    // Stop current message polling
    get().stopMessagePolling();
    
    set({ activeChat: chat });
    
    // Load messages for the active chat
    if (chat) {
      get().loadMessages(chat.userId);
      // Start message polling for the active chat
      get().startMessagePolling(chat.userId);
    }
  },

  addMessage: (message: Message) => {
    // This will be used for optimistic UI updates
    set((state) => {
      const activeChat = state.activeChat;
      if (!activeChat) return state;
      
      return {
        messages: {
          ...state.messages,
          [activeChat.userId]: [
            ...(state.messages[activeChat.userId] || []),
            message
          ]
        }
      };
    });
  },

  updateMessage: (messageId: string, updates: Partial<Message>) => {
    set((state) => {
      const newMessages = { ...state.messages };
      
      Object.keys(newMessages).forEach(userId => {
        newMessages[userId] = newMessages[userId].map(msg =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        );
      });
      
      return { messages: newMessages };
    });
  },

  updateChatStatus: async (userId: number, status: 'pending' | 'read') => {
    try {
      if (status === 'read') {
        await apiService.markChatAsRead(userId);
        
        // Update the chat status in the store
        set((state) => {
          // Remove from notified set when marked as read
          const updatedNotified = new Set(state.notifiedUnreadChats);
          updatedNotified.delete(userId);
          
          return {
            chats: state.chats.map(chat =>
              chat.userId === userId ? { ...chat, status: 'read', unreadCount: 0 } : chat
            ),
            activeChat: state.activeChat?.userId === userId 
              ? { ...state.activeChat, status: 'read', unreadCount: 0 }
              : state.activeChat,
            notifiedUnreadChats: updatedNotified
          };
        });
        
        toast.success('Chat marked as read');
      } else if (status === 'pending') {
        await apiService.markChatAsPending(userId, true);
        
        // Update the chat status in the store
        set((state) => ({
          chats: state.chats.map(chat =>
            chat.userId === userId ? { ...chat, status: 'pending' } : chat
          ),
          activeChat: state.activeChat?.userId === userId 
            ? { ...state.activeChat, status: 'pending' }
            : state.activeChat
        }));
        
        toast.success('Chat marked as pending');
      }
    } catch (error: any) {
      console.error('Failed to update chat status:', error);
      toast.error(error.message || 'Failed to update chat status');
    }
  },

  clearError: () => set({ error: null }),
  
  setLoading: (loading: boolean) => set({ isLoading: loading }),

  // Search state setters
  setSearchQuery: (query: string) => set({ currentSearchQuery: query }),
  setFilterType: (type: string) => set({ currentFilterType: type }),

  // Trip filter state setters
  setSelectedTripIds: (tripIds: string[]) => set({ selectedTripIds: tripIds }),
  
  loadSelectedTripIds: () => {
    try {
      const savedTripIds = localStorage.getItem('selected_trip_ids');
      if (savedTripIds) {
        const tripIds = savedTripIds.split(',').map(id => id.trim()).filter(id => id);
        set({ selectedTripIds: tripIds });
      }
    } catch (error) {
      console.error('Failed to load selected trip IDs from localStorage:', error);
    }
  },
  
  saveSelectedTripIds: (tripIds: string[]) => {
    try {
      if (tripIds.length > 0) {
        localStorage.setItem('selected_trip_ids', tripIds.join(','));
      } else {
        localStorage.removeItem('selected_trip_ids');
      }
      set({ selectedTripIds: tripIds });
    } catch (error) {
      console.error('Failed to save selected trip IDs to localStorage:', error);
    }
  },

  // Loading state setters
  setLoadingChatId: (chatId: number | null) => set({ loadingChatId: chatId }),

  // Polling implementation
  startPolling: () => {
    const { isPolling } = get();
    if (isPolling) return;

    set({ isPolling: true });

    const pollInterval = setInterval(async () => {
      const { isPolling: stillPolling, lastUpdatedOn } = get();
      
      // Stop if polling was disabled
      if (!stillPolling) {
        clearInterval(pollInterval);
        return;
      }

      // Pause if tab is not visible
      if (document.visibilityState === 'hidden') {
        return;
      }

      try {
        // Get current search state and trip filter state
        const { currentSearchQuery, currentFilterType, selectedTripIds } = get();
        
        // Fetch incremental updates with current search query, filter type, and trip IDs
        // Pass empty string instead of undefined for better API handling
        const searchQuery = currentSearchQuery.trim() || undefined;
        const filterType = currentFilterType === 'ALL' ? undefined : currentFilterType;
        const tripIds = selectedTripIds.length > 0 ? selectedTripIds : undefined;
        
        const updatedChats = await apiService.getChats(searchQuery, filterType, lastUpdatedOn, tripIds);
        
        if (updatedChats && updatedChats.length > 0) {
          set((state) => {
            const existingChats = state.chats;
            const newChats = [...existingChats];
            
            // Merge updates: replace existing or add new
            updatedChats.forEach(updatedChat => {
              const existingIndex = newChats.findIndex(chat => chat.userId === updatedChat.userId);
              if (existingIndex >= 0) {
                // Replace existing chat
                newChats[existingIndex] = updatedChat;
              } else {
                // Add new chat
                newChats.push(updatedChat);
              }
            });

            // Sort by unread status first, then by lastMessageTime (newest first)
            newChats.sort((a, b) => {
              // First priority: unread chats (unreadCount > 0)
              const aHasUnread = a.unreadCount > 0;
              const bHasUnread = b.unreadCount > 0;
              
              if (aHasUnread && !bHasUnread) return -1;
              if (!aHasUnread && bHasUnread) return 1;
              
              // Second priority: lastMessageTime (newest first)
              return b.lastMessageTime - a.lastMessageTime;
            });

            // Update lastUpdatedOn to the most recent timestamp
            const mostRecentTime = Math.max(
              ...updatedChats.map(chat => chat.lastMessageTime),
              lastUpdatedOn || 0
            );

            // Save updated chats to local storage (only for default load)
            const { currentSearchQuery, currentFilterType, selectedTripIds } = state;
            const isDefaultLoad = !currentSearchQuery && !currentFilterType && !selectedTripIds.length;
            if (isDefaultLoad) {
              saveToLocalStorage(STORAGE_KEYS.CHATS, newChats);
              saveToLocalStorage(STORAGE_KEYS.LAST_SYNC, Date.now());
            }

            return {
              chats: newChats,
              lastUpdatedOn: mostRecentTime
            };
          });

          // Check for unread chats and play notification if needed
          get().checkForUnreadChats(newChats);

          // Auto-load messages for unread chats if not in local storage
          get().loadUnreadChatMessages();
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling even on error - will retry in 2 seconds
      }
    }, 2000);

    // Store interval ID for cleanup
    set({ pollInterval });
  },

  stopPolling: () => {
    const { isPolling, pollInterval } = get();
    if (isPolling && pollInterval) {
      clearInterval(pollInterval);
      set({ isPolling: false, pollInterval: null });
    }
  },

  // Message polling implementation
  startMessagePolling: (userId: number) => {
    const { isMessagePolling } = get();
    if (isMessagePolling) return;

    set({ isMessagePolling: true });

    const pollInterval = setInterval(async () => {
      const { isMessagePolling: stillPolling, lastMessageUpdatedOn, activeChat } = get();
      
      // Stop if polling was disabled or active chat changed
      if (!stillPolling || !activeChat || activeChat.userId !== userId) {
        clearInterval(pollInterval);
        return;
      }

      // Pause if tab is not visible
      if (document.visibilityState === 'hidden') {
        return;
      }

      try {
        // Fetch all messages for this user (API doesn't support incremental updates for messages)
        const updatedMessages = await apiService.getMessages(userId);
        
        if (updatedMessages && updatedMessages.length > 0) {
          set((state) => {
            const existingMessages = state.messages[userId] || [];
            
            // Check if we have new messages by comparing lengths or timestamps
            const hasNewMessages = updatedMessages.length > existingMessages.length ||
              (updatedMessages.length > 0 && existingMessages.length > 0 &&
               updatedMessages[updatedMessages.length - 1].createdOn > existingMessages[existingMessages.length - 1].createdOn);
            
            if (hasNewMessages) {
              // Update the most recent message timestamp
              const mostRecentTime = Math.max(...updatedMessages.map(msg => msg.createdOn));
              
              // Save to local storage
              const allCachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES) || {};
              allCachedMessages[userId] = updatedMessages;
              saveToLocalStorage(STORAGE_KEYS.MESSAGES, allCachedMessages);
              
              return {
                messages: {
                  ...state.messages,
                  [userId]: updatedMessages
                },
                lastMessageUpdatedOn: {
                  ...state.lastMessageUpdatedOn,
                  [userId]: mostRecentTime
                }
              };
            }
            
            return state;
          });
        }
      } catch (error) {
        console.error('Message polling error:', error);
        // Continue polling even on error - will retry in 5 seconds
      }
    }, 5000);

    // Store interval ID for cleanup
    set({ messagePollInterval: pollInterval });
  },

  stopMessagePolling: () => {
    const { isMessagePolling, messagePollInterval } = get();
    if (isMessagePolling && messagePollInterval) {
      clearInterval(messagePollInterval);
      set({ isMessagePolling: false, messagePollInterval: null });
    }
  },

  // Audio notification for unread chats
  checkForUnreadChats: (newChats: Chat[]) => {
    // Only play sound if audio notifications are enabled
    if (!audioNotificationManager.isEnabled()) {
      return;
    }

    // Check if there are any unread chats
    const hasUnreadChats = newChats.some(chat => chat.unreadCount > 0);
    
    if (hasUnreadChats) {
      // Play beep sound whenever there are unread chats available
      audioNotificationManager.playBeep({ volume: 0.2 });
    }
    
    // Update notified set to track which chats are currently unread
    const currentlyUnreadUserIds = new Set(
      newChats.filter(chat => chat.unreadCount > 0).map(chat => chat.userId)
    );
    set({ notifiedUnreadChats: currentlyUnreadUserIds });
  },

  // Clear all local storage data
  clearLocalStorage: () => {
    clearLocalStorage(STORAGE_KEYS.CHATS);
    clearLocalStorage(STORAGE_KEYS.MESSAGES);
    clearLocalStorage(STORAGE_KEYS.CHAT_METADATA);
    clearLocalStorage(STORAGE_KEYS.LAST_SYNC);
    console.log('Local storage cleared');
  },

  // Get cache statistics
  getCacheStats: () => {
    const chats = loadFromLocalStorage(STORAGE_KEYS.CHATS);
    const messages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES);
    const lastSync = loadFromLocalStorage(STORAGE_KEYS.LAST_SYNC);
    
    return {
      hasChats: !!chats && chats.length > 0,
      chatCount: chats ? chats.length : 0,
      hasMessages: !!messages && Object.keys(messages).length > 0,
      messageUserCount: messages ? Object.keys(messages).length : 0,
      lastSync: lastSync ? new Date(lastSync).toLocaleString() : 'Never'
    };
  },

  // Set bulk loading progress
  setBulkLoadingProgress: (progress: number, total: number, current: number, message: string) => {
    set({
      bulkLoadingProgress: progress,
      bulkLoadingTotal: total,
      bulkLoadingCurrent: current,
      bulkLoadingMessage: message
    });
  },

  // Load all chats with their messages (bulk loading)
  loadAllChatsWithMessages: async () => {
    // Clear all chats from local storage on first load to free up space
    clearLocalStorage(STORAGE_KEYS.CHATS);
    clearLocalStorage(STORAGE_KEYS.MESSAGES);
    clearLocalStorage(STORAGE_KEYS.CHAT_METADATA);
    clearLocalStorage(STORAGE_KEYS.LAST_SYNC);
    console.log('Cleared all chats from local storage on first load');
    
    set({ 
      isBulkLoading: true, 
      bulkLoadingProgress: 0, 
      bulkLoadingTotal: 0, 
      bulkLoadingCurrent: 0,
      bulkLoadingMessage: 'Loading chats...',
      error: null 
    });

    try {
      // Always fetch fresh chats from API (cache cleared above)
      get().setBulkLoadingProgress(0, 100, 0, 'Fetching chat list...');
      const chats = await apiService.getChats();
      console.log(`Fetched chats from API: ${chats.length} chats`);
      
      // Save chats to cache for future use
      saveToLocalStorage(STORAGE_KEYS.CHATS, chats);
      saveToLocalStorage(STORAGE_KEYS.LAST_SYNC, Date.now());
      
      if (!chats || chats.length === 0) {
        set({ 
          isBulkLoading: false, 
          chats: [], 
          bulkLoadingMessage: 'No chats found' 
        });
        return;
      }

      // Update chats in store
      set({ chats });
      
      // Limit to first 25 chats for message loading
      const chatsToLoad = chats.slice(0, 25);
      const totalSteps = chatsToLoad.length + 1; // +1 for initial step
      let currentStep = 1;
      
      // Load messages for each chat (limited to first 25)
      const allMessages: { [userId: number]: Message[] } = {};
      
      for (let i = 0; i < chatsToLoad.length; i++) {
        const chat = chatsToLoad[i];
        currentStep = i + 2; // +2 because we already did step 1 (loading chats)
        const progress = Math.round((currentStep / totalSteps) * 100);
        
        get().setBulkLoadingProgress(
          progress, 
          totalSteps, 
          currentStep, 
          `Loading messages for ${chat.name}...`
        );

        try {
          // Check cache first
          const cachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES);
          let messages: Message[] = [];
          
          if (cachedMessages && cachedMessages[chat.userId]) {
            messages = cachedMessages[chat.userId];
            console.log(`Loaded ${messages.length} messages from cache for ${chat.name}`);
          } else {
            // Fetch from API
            messages = await apiService.getMessages(chat.userId);
            console.log(`Fetched ${messages.length} messages for ${chat.name}`);
            
            // Save to cache
            const allCachedMessages = loadFromLocalStorage(STORAGE_KEYS.MESSAGES) || {};
            allCachedMessages[chat.userId] = messages;
            saveToLocalStorage(STORAGE_KEYS.MESSAGES, allCachedMessages);
          }
          
          allMessages[chat.userId] = messages;
          
          // Small delay to prevent overwhelming the UI
          await new Promise(resolve => setTimeout(resolve, 50));
          
        } catch (error) {
          console.error(`Failed to load messages for ${chat.name}:`, error);
          allMessages[chat.userId] = [];
        }
      }

      // Update messages in store
      set((state) => ({
        messages: { ...state.messages, ...allMessages },
        isBulkLoading: false,
        bulkLoadingMessage: 'Loading complete!'
      }));

      console.log(`Bulk loading completed: ${chats.length} total chats, ${chatsToLoad.length} chats with messages loaded, ${Object.keys(allMessages).length} users with messages`);

    } catch (error) {
      console.error('Bulk loading failed:', error);
      set({ 
        isBulkLoading: false, 
        error: 'Failed to load chats and messages',
        bulkLoadingMessage: 'Loading failed'
      });
    }
  },
}));
